import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from 'react';
import { InvoicePaymentDisplayStatusType, InvoiceDisplayStatusV2, ISSUES_PREVENTING_PAYMENT } from 'anchor-scout-common/types';
import colors from 'seagull/foundations/colors.module.scss';
import GenericError from '~/components/GenericError/GenericError';
import { routes } from '~/constants';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { InvoicesDigestModel } from '~/models/invoices-digest/InvoicesDigestModel';
import GraphWidgetBottomCard from '~/pages/VendorDashboard/GraphWidget/GraphWidgetBottomCard';
import GraphWidgetLayout from '~/pages/VendorDashboard/GraphWidget/GraphWidgetLayout';
import InvoiceBar from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoiceBar';
import InvoicesInfoTable from '~/pages/VendorDashboard/InvoicesDigestWidget/InvoicesDigestTable';
import { useSubscribeToBillingObservable } from '~/providers/BillingObservableProvider';
import ThreeLineTopCard from '../GraphWidget/ThreeLineTopCard';
import { useInvoicesDigest } from './hooks/useInvoicesDigest';
import styles from './InvoicesDigestWidget.module.scss';
import { invoicePaymentStatusMap } from './InvoicesDigestWIdget.utils';
const makeNewInvoiceStatusGroup = (count, amount)=>{
    return {
        count,
        amount: {
            amount
        }
    };
};
export function mockInvoiceDigestApi() {
    let { hasCreatedInvoice } = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {
        hasCreatedInvoice: true
    };
    return {
        statuses: {
            requiresClientApproval: makeNewInvoiceStatusGroup(10, 1500000),
            paymentsVerificationRequired: makeNewInvoiceStatusGroup(10, 1500000),
            missingPaymentMethod: makeNewInvoiceStatusGroup(10, 1500000),
            failed: makeNewInvoiceStatusGroup(10, 1500000),
            disputed: makeNewInvoiceStatusGroup(10, 1500000),
            paymentStopped: makeNewInvoiceStatusGroup(0, 0),
            paymentMethodUnverified: makeNewInvoiceStatusGroup(10, 1500000)
        },
        processing: makeNewInvoiceStatusGroup(123, 12300),
        requiresAttention: makeNewInvoiceStatusGroup(123, 12300),
        hasCreatedInvoice: !!hasCreatedInvoice
    };
}
function MockInvoicesDigestBottomCard() {
    const invoiceDigest = useMemo(()=>InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()), []);
    return renderBottomCardContent(invoiceDigest);
}
const renderBottomCardContent = (invoicesDigest)=>/*#__PURE__*/ _jsxs("div", {
        className: styles.bottomCardContent,
        children: [
            /*#__PURE__*/ _jsx(InvoiceBar, {
                invoicesDigest: invoicesDigest
            }),
            /*#__PURE__*/ _jsx(InvoicesInfoTable, {
                invoiceDigestModel: invoicesDigest
            })
        ]
    });
export const invoiceStatusToColor = {
    requiresClientApproval: '#6A9FF0',
    paymentsVerificationRequired: colors.purple300,
    missingPaymentMethod: '#A9E5F2',
    failed: '#FDA5EF',
    disputed: colors.red700,
    paymentStopped: '#e7049e',
    paymentMethodUnverified: '#FBD499'
};
const InvoicesDigestWidget = (param)=>{
    let { invoicesDigest, isRefreshing, isFirstTimeLoading } = param;
    const { t } = useLocaleKeys('pages.dashboard.invoicesDigestWidget');
    const { INVOICE_STATUSES_V2 } = useFeatureFlags();
    const testIdPrefix = 'invoices-digest';
    const processingLinkTo = INVOICE_STATUSES_V2 ? "".concat(routes.invoicesDashboard, "?displayStatusV2=").concat(InvoiceDisplayStatusV2.Processing) : "".concat(routes.invoicesDashboard, "?paymentStatus=").concat(InvoicePaymentDisplayStatusType.Processing, ",").concat(InvoicePaymentDisplayStatusType.Collected);
    const requireAttentionLinkTo = INVOICE_STATUSES_V2 ? "".concat(routes.invoicesDashboard, "?paymentDetails=").concat(ISSUES_PREVENTING_PAYMENT.join(',')) : "".concat(routes.invoicesDashboard, "?paymentStatus=").concat(Object.values(invoicePaymentStatusMap).join(','));
    return /*#__PURE__*/ _jsxs(GraphWidgetLayout, {
        loading: isRefreshing,
        title: t.title(),
        bottomHeight: "l",
        children: [
            /*#__PURE__*/ _jsx(ThreeLineTopCard, {
                loading: isFirstTimeLoading,
                title: t.processing.title(),
                success: true,
                testId: "".concat(testIdPrefix, "-processing"),
                titleInfo: /*#__PURE__*/ _jsx("div", {
                    "data-testid": "".concat(testIdPrefix, "-processing-tooltip"),
                    children: t.processing.tooltip()
                }),
                secondaryText: t.invoicesCount({
                    count: invoicesDigest.processing().count
                }),
                primaryText: invoicesDigest.processing().amount.display(),
                linkTo: processingLinkTo
            }),
            /*#__PURE__*/ _jsx(ThreeLineTopCard, {
                loading: isFirstTimeLoading,
                title: t.requireAttention.title(),
                testId: "".concat(testIdPrefix, "-require-attention"),
                secondaryText: t.invoicesCount({
                    count: invoicesDigest.requiresAttention().count
                }),
                titleInfo: /*#__PURE__*/ _jsx("div", {
                    "data-testid": "".concat(testIdPrefix, "-require-attention-tooltip"),
                    children: t.requireAttention.tooltip()
                }),
                primaryText: invoicesDigest.requiresAttention().amount.display(),
                linkTo: requireAttentionLinkTo
            }),
            /*#__PURE__*/ _jsx(GraphWidgetBottomCard, {
                testId: "invoices-digest-bottom-card",
                loadingOrEmptyBg: ()=>!invoicesDigest.hasCreatedInvoice() || isFirstTimeLoading ? /*#__PURE__*/ _jsx(MockInvoicesDigestBottomCard, {}) : null,
                loading: isFirstTimeLoading,
                loadingMsg: t.loadingMsg(),
                empty: invoicesDigest.numInvoiceTypes() === 0,
                emptyMsg: invoicesDigest.hasCreatedInvoice() ? /*#__PURE__*/ _jsx("div", {
                    "data-testid": "invoice-ever-created-covering-text",
                    children: t.noAttentionRequired()
                }) : /*#__PURE__*/ _jsx("div", {
                    "data-testid": "never-created-invoice-covering-text",
                    children: t.viewAttentionRequired()
                }),
                children: renderBottomCardContent(invoicesDigest)
            })
        ]
    });
};
export default function InvoicesDigestWidgetFetch() {
    const { invoicesDigest, isLoading, isBackgroundLoading, refresh, error } = useInvoicesDigest({
        refreshOnHookMount: true
    });
    useSubscribeToBillingObservable(async ()=>{
        refresh();
    });
    if (error) return /*#__PURE__*/ _jsx(GenericError, {
        testId: "widget-error"
    });
    if (isLoading) {
        return /*#__PURE__*/ _jsx(InvoicesDigestWidget, {
            invoicesDigest: InvoicesDigestModel.fromDTO(mockInvoiceDigestApi()),
            isRefreshing: true,
            isFirstTimeLoading: true
        });
    }
    return /*#__PURE__*/ _jsx(InvoicesDigestWidget, {
        invoicesDigest: invoicesDigest,
        isRefreshing: isBackgroundLoading,
        isFirstTimeLoading: false
    });
}
